import type { ComponentType, CSSProperties } from 'react';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  FileTextOutlined,
  FolderOutlined,
  HomeOutlined,
  IdcardOutlined,
  KeyOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SoundOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';

import type { WikiMeDto } from './authApi';

/**
 * Модель меню FISHKA HUB 2.0 (волна W1, фаза 0; план wk6 §1.1).
 *
 * Сгруппированное меню «Для меня / Знания / Обучение / Управление» вместо
 * плоских NAV + ADMIN_NAV в Layout.tsx. Layout перерисовывается в фазе 1 —
 * здесь только данные: пункты, видимость по роли/праву (как в текущем
 * Layout.tsx), ключ бейджа. Этот же список берут CmdK (группа «Разделы»),
 * нижняя панель телефона и меню пользователя.
 *
 * URL не переименовываем (план §0.1): ссылки лежат в wiki_notifications.link,
 * Telegram/MAX и закладках. «Профиль» ушёл в меню пользователя; «Роли и
 * права», «Критерии помещения», «Архив статей» станут вкладками (W5).
 */

/** Ключи бейджей-счётчиков (фаза 1: один запрос GET /wiki/hub/counts). */
export type NavBadgeKey = 'tasks' | 'news' | 'standards' | 'reviews' | 'freshness' | 'questions';

export type NavIcon = ComponentType<{ style?: CSSProperties; className?: string }>;

export interface NavItem {
  /** Маршрут (он же ключ пункта). */
  to: string;
  label: string;
  icon: NavIcon;
  /** Ключ бейджа (значение — из счётчиков Layout). */
  badge?: NavBadgeKey;
  /** Видимость пункта; нет функции — виден всем вошедшим. */
  visible?: (me: WikiMeDto | undefined) => boolean;
  /**
   * Дополнительные префиксы, при которых пункт подсвечен активным
   * (база знаний — и на /block, /article, /search).
   */
  activePrefixes?: string[];
  /** Синонимы для поиска раздела в CmdK. */
  keywords?: string[];
}

export interface NavGroup {
  key: 'me' | 'knowledge' | 'learning' | 'admin';
  /** Заголовок группы (в меню — капсом). */
  title: string;
  /** Видимость группы целиком. */
  visible?: (me: WikiMeDto | undefined) => boolean;
  items: NavItem[];
}

// ── Предикаты ролей (повторяют текущий Layout.tsx / HomePage.resolveAudience) ──

/** УК: право kb:admin. */
export const isUk = (me: WikiMeDto | undefined): boolean => !!me?.canAdmin;
/** Проверяющий ответы: УК или управляющий с правом проверки. */
export const canReviewAnswers = (me: WikiMeDto | undefined): boolean => !!me?.canAdmin || !!me?.canReview;
/** Партнёр — CRM-пользователь с ролью PARTNER. */
export const isPartner = (me: WikiMeDto | undefined): boolean => me?.type === 'crm_user' && me.roleCode === 'PARTNER';
/** Видит открытия партнёров: право CRM «Этапы открытия — просмотр» (сервер гасит флаг в режиме «от лица»). */
export const canViewOpenings = (me: WikiMeDto | undefined): boolean => !!me?.canViewOpenings;
/** «Открытие точки» нужна партнёру (свой план) и сотруднику УК с правом просмотра открытий (план каждого партнёра). */
export const hasOpening = (me: WikiMeDto | undefined): boolean => isPartner(me) || canViewOpenings(me);
/** Наставник: УК, CRM-сотрудники кроме партнёра, iiko-управляющие (как Layout сейчас). */
export const canMentor = (me: WikiMeDto | undefined): boolean =>
  !!me?.canAdmin || (me?.type === 'crm_user' && me.roleCode !== 'PARTNER') || me?.roleCode === 'MANAGER';
/** «Партнёры сети» (досье партнёров): партнёр или сотрудник УК; флаг считает сервер (`dossier.network`). */
export const canSeePartnerNetwork = (me: WikiMeDto | undefined): boolean => !!me?.dossier?.network;
/** «Моё досье»: только партнёр со своим ИП (`dossier.mine`). */
export const hasOwnDossier = (me: WikiMeDto | undefined): boolean => !!me?.dossier?.mine;
/** Сотрудник iiko (вход по телефону) — только он меняет пароль в вики. */
export const isIikoEmployee = (me: WikiMeDto | undefined): boolean => me?.type === 'iiko_employee';

/** Аудитория главной (как resolveAudience в HomePage). */
export type Audience = 'uk' | 'partner' | 'manager' | 'employee';
export function audienceOf(me: WikiMeDto | undefined): Audience {
  if (!me) return 'employee';
  if (me.canAdmin) return 'uk';
  if (me.type === 'crm_user') return me.roleCode === 'PARTNER' ? 'partner' : 'manager';
  return me.roleCode === 'MANAGER' ? 'manager' : 'employee';
}

// ── Меню ──────────────────────────────────────────────────────────────

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'me',
    title: 'Для меня',
    items: [
      { to: '/', label: 'Главная', icon: HomeOutlined, keywords: ['домой', 'старт'] },
      { to: '/tasks', label: 'Мои задачи', icon: CheckSquareOutlined, badge: 'tasks', keywords: ['задачи', 'дедлайны'] },
      { to: '/news', label: 'Новости', icon: SoundOutlined, badge: 'news', keywords: ['лента', 'объявления'] },
      { to: '/partners', label: 'Партнёры сети', icon: ContactsOutlined, visible: canSeePartnerNetwork, keywords: ['досье', 'знакомство', 'дни рождения', 'хобби', 'коллеги'] },
    ],
  },
  {
    key: 'knowledge',
    title: 'Знания',
    items: [
      { to: '/knowledge', label: 'База знаний', icon: BookOutlined, activePrefixes: ['/block/', '/article/', '/search'], keywords: ['статьи', 'инструкции', 'вики'] },
      { to: '/standards', label: 'Стандарты', icon: SafetyCertificateOutlined, badge: 'standards', keywords: ['ознакомиться', 'регламенты'] },
      { to: '/disk', label: 'Диск', icon: FolderOutlined, keywords: ['файлы', 'документы', 'шаблоны'] },
      { to: '/ai', label: 'AI-помощник', icon: ThunderboltOutlined, keywords: ['ии', 'gpt', 'спросить'] },
    ],
  },
  {
    key: 'learning',
    title: 'Обучение',
    items: [
      { to: '/education', label: 'Моё обучение', icon: ReadOutlined, activePrefixes: ['/course/', '/lesson/', '/qod'], keywords: ['курсы', 'тесты', 'уроки'] },
      { to: '/path', label: 'Траектория', icon: NodeIndexOutlined, keywords: ['путь', 'адаптация', 'план обучения'] },
      { to: '/mentor', label: 'Наставник', icon: TeamOutlined, visible: canMentor, keywords: ['команда', 'чек-лист', 'навыки'] },
      { to: '/opening', label: 'Открытие точки', icon: BankOutlined, visible: hasOpening, keywords: ['открытие', 'помещение', 'этапы'] },
    ],
  },
  {
    key: 'admin',
    title: 'Управление',
    visible: canReviewAnswers,
    items: [
      { to: '/admin/courses', label: 'Конструктор курсов', icon: AppstoreOutlined, visible: isUk, keywords: ['курс', 'создать курс', 'scorm'] },
      { to: '/admin/paths', label: 'Траектории обучения', icon: ApartmentOutlined, visible: isUk, activePrefixes: ['/admin/skills'], keywords: ['траектории', 'навыки'] },
      { to: '/admin/reviews', label: 'Проверка ответов', icon: CheckSquareOutlined, badge: 'reviews', visible: canReviewAnswers, keywords: ['проверка', 'практика', 'фото'] },
      { to: '/admin/analytics', label: 'Аналитика', icon: BarChartOutlined, visible: isUk, keywords: ['статистика', 'отчёты'] },
      { to: '/admin/freshness', label: 'Актуальность базы', icon: ClockCircleOutlined, badge: 'freshness', visible: isUk, keywords: ['устаревшие', 'проверить статьи'] },
      { to: '/admin/questions', label: 'Обращения и вопросы', icon: MessageOutlined, badge: 'questions', visible: isUk, keywords: ['вопросы партнёров', 'обращения'] },
      { to: '/admin/users', label: 'Пользователи и роли', icon: TeamOutlined, visible: isUk, activePrefixes: ['/admin/roles'], keywords: ['сотрудники', 'роли', 'права'] },
      { to: '/admin/wiki', label: 'Администрирование', icon: SettingOutlined, visible: isUk, activePrefixes: ['/admin/archive', '/admin/room-criteria', '/admin/documents', '/admin/inbox', '/admin/suggested-edits', '/graph'], keywords: ['настройки', 'разделы', 'теги', 'архив'] },
    ],
  },
];

/** Меню пользователя (кнопка с инициалами в шапке). */
export const USER_MENU: NavItem[] = [
  { to: '/profile', label: 'Профиль', icon: UserOutlined },
  { to: '/dossier', label: 'Моё досье', icon: IdcardOutlined, visible: hasOwnDossier, keywords: ['анкета', '10 фактов', 'о себе', 'фото', 'хобби', 'день рождения'] },
  { to: '/points', label: 'Мои очки и бейджи', icon: TrophyOutlined, keywords: ['рейтинг', 'бейджи', 'уровень'] },
  { to: '/notifications', label: 'Уведомления', icon: BellOutlined },
  { to: '/documents', label: 'Мои документы', icon: FileTextOutlined },
  { to: '/change-password', label: 'Сменить пароль', icon: KeyOutlined, visible: isIikoEmployee },
];

/**
 * Нижняя панель на телефоне (<768 px, фаза 1): 5 вкладок. «Ещё» открывает
 * drawer с полным меню (to = '#more' — Layout перехватывает).
 */
export const MOBILE_TABS: NavItem[] = [
  { to: '/', label: 'Главная', icon: HomeOutlined },
  { to: '/education', label: 'Обучение', icon: ReadOutlined, activePrefixes: ['/course/', '/lesson/', '/path', '/qod'] },
  { to: '/knowledge', label: 'База', icon: BookOutlined, activePrefixes: ['/block/', '/article/', '/search', '/standards', '/disk'] },
  { to: '/tasks', label: 'Задачи', icon: CheckSquareOutlined, badge: 'tasks' },
  { to: '#more', label: 'Ещё', icon: AppstoreOutlined },
];

// ── Помощники ───────────────────────────────────────────────────────

const itemVisible = (item: NavItem, me: WikiMeDto | undefined): boolean => (item.visible ? item.visible(me) : true);

/** Видимые группы с видимыми пунктами (пустые группы выкидываются). */
export function visibleNav(me: WikiMeDto | undefined): NavGroup[] {
  return NAV_GROUPS.filter((g) => (g.visible ? g.visible(me) : true))
    .map((g) => ({ ...g, items: g.items.filter((i) => itemVisible(i, me)) }))
    .filter((g) => g.items.length > 0);
}

/** Видимые пункты меню пользователя. */
export function visibleUserMenu(me: WikiMeDto | undefined): NavItem[] {
  return USER_MENU.filter((i) => itemVisible(i, me));
}

/** Все видимые разделы плоским списком — для CmdK «Разделы». */
export function navSearchItems(me: WikiMeDto | undefined): NavItem[] {
  return [...visibleNav(me).flatMap((g) => g.items), ...visibleUserMenu(me)];
}

/** Активен ли пункт для текущего пути. */
export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.to === '/') return pathname === '/';
  if (item.to.startsWith('#')) return false;
  const hit = (p: string): boolean => pathname === p || pathname.startsWith(p.endsWith('/') ? p : `${p}/`);
  return hit(item.to) || (item.activePrefixes ?? []).some((p) => pathname.startsWith(p));
}

/**
 * Название текущей страницы для «Где это случилось» в форме «Написать в УК»
 * (уходит в CRM «Обращения» разделом «HUB · <страница>»). Сначала — точные
 * экраны без пункта меню (статья, курс, урок…), затем пункт меню, затем «HUB».
 */
const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/article\//, 'Статья'],
  [/^\/block\//, 'База знаний · раздел'],
  [/^\/search/, 'Поиск'],
  [/^\/knowledge\/ask/, 'Вопрос по базе'],
  [/^\/course\/[^/]+\/quiz\//, 'Тест'],
  [/^\/course\//, 'Курс'],
  [/^\/lesson\//, 'Урок'],
  [/^\/qod/, 'Вопрос дня'],
  [/^\/opening\/room-check/, 'Открытие точки · помещение'],
  [/^\/admin\/courses\/new/, 'Мастер курса'],
  [/^\/admin\/skills/, 'Навыки'],
  [/^\/admin\/roles/, 'Роли и права'],
  [/^\/admin\/archive/, 'Архив статей'],
  [/^\/admin\/room-criteria/, 'Критерии помещения'],
  [/^\/admin\/documents/, 'Шаблоны документов'],
  [/^\/admin\/inbox/, 'Импорт'],
  [/^\/admin\/suggested-edits/, 'Предложенные правки'],
  [/^\/admin\/opening/, 'Открытия точек'],
  [/^\/admin\/decision-tree\//, 'Дерево решений'],
  [/^\/admin\/design/, 'Дизайн-система'],
  [/^\/graph/, 'Граф знаний'],
];

export function pageTitleOf(pathname: string): string {
  for (const [re, title] of PAGE_TITLES) if (re.test(pathname)) return title;
  const all = [...NAV_GROUPS.flatMap((g) => g.items), ...USER_MENU];
  // Точное совпадение пути важнее префиксов (/admin/paths vs /path).
  const exact = all.find((i) => i.to === pathname);
  if (exact) return exact.label;
  const hit = all.find((i) => isNavActive(i, pathname));
  return hit?.label ?? 'HUB';
}

/** Нормализация для поиска разделов: регистр и «ё». */
const normQ = (s: string): string => s.toLowerCase().replace(/ё/g, 'е').trim();

/** CmdK «Разделы»: пункты меню, подходящие под запрос (название и синонимы), не больше 4. */
export function matchSections(items: Array<Pick<NavItem, 'to' | 'label' | 'keywords'>>, q: string): Array<{ to: string; label: string }> {
  const n = normQ(q);
  if (n.length < 2) return [];
  return items
    .filter((i) => [i.label, ...(i.keywords ?? [])].some((w) => normQ(w).includes(n)))
    .slice(0, 4)
    .map((i) => ({ to: i.to, label: i.label }));
}
